// Idle timeout: after `idle_timeout_seconds` with no user input in any tab, the session ends with a
// full logout. The last-activity stamp lives in localStorage, so every tab reads and writes the same
// deadline, and it outlives the tab: a boot-time check catches a user who idled by closing the tab
// instead. Deadlines compare timestamps, never trust a timer, so throttled background tabs and a
// sleeping laptop still expire on time.
const KEY = 'cenorion.auth.lastActivityAt';

/** Module-scoped, like the OIDC state in `oidc.ts`: set once at boot from the auth config. */
let timeoutMs: number | null = null;

/** `undefined` (absent or invalid in the config) turns the feature off. */
export function setIdleTimeout(seconds: number | undefined): void {
  timeoutMs = seconds ? seconds * 1000 : null;
}

export function getIdleTimeoutMs(): number | null {
  return timeoutMs;
}

/**
 * The last recorded activity, or null when there is no usable stamp. A NaN or future value is
 * null too: absence is not evidence of idleness, and treating it as such would log out every user
 * on the first deploy (or after a clock correction).
 */
export function readLastActivity(now = Date.now()): number | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value <= now ? value : null;
  } catch {
    // Storage unavailable reads as "fresh": a broken stamp must never be what logs someone out.
    return null;
  }
}

export function stampActivity(now = Date.now()): void {
  try {
    window.localStorage.setItem(KEY, String(now));
  } catch {
    // Storage unavailable — the timeout cannot be tracked, so it never fires.
  }
}

/** Null `last` is never expired — see `readLastActivity`. */
export function isIdleExpired(last: number | null, timeout: number, now: number): boolean {
  return last !== null && now - last >= timeout;
}
